import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Mail } from 'lucide-react';
import { useLanguage } from '../../contexts/language/LanguageContext';
import { useNavbar } from '../../contexts/navigation/NavbarContext';
import { InstagramIcon } from '../icons';
import { CONTACT_INFO } from '../../helpers/constants.helper';
import './MobileMenu.scss';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileMenu = ({ isOpen, onClose }: MobileMenuProps) => {
  const { t, language, setLanguage } = useLanguage();
  const { goToPortfolioSection } = useNavbar();
  const location = useLocation();

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      // Store original scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore scroll position
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Close on ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleLanguageChange = (lang: 'en' | 'uk') => {
    setLanguage(lang);
    onClose();
  };

  // Handle portfolio click - use callback if already on home page
  const handlePortfolioClick = (e: React.MouseEvent) => {
    const isHomePage = location.pathname === '/' || location.pathname === '/portfolio';
    onClose();
    if (isHomePage && goToPortfolioSection) {
      e.preventDefault();
      goToPortfolioSection();
    }
  };

  return (
    <div className={`mobile-menu ${isOpen ? 'open' : ''}`}>
      <div className="mobile-menu__overlay" onClick={onClose} />

      <div className="mobile-menu__container">
        <button className="mobile-menu__close" onClick={onClose} aria-label="Close menu">
          <X size={32} strokeWidth={1} />
        </button>

        <nav className="mobile-menu__nav">
          {/* Main Links */}
          <Link
            to="/portfolio"
            className="mobile-menu__link"
            onClick={handlePortfolioClick}
          >
            {t.nav.portfolio}
          </Link>
          <Link to="/services" className="mobile-menu__link" onClick={onClose}>
            {t.nav.terms}
          </Link>
          <Link to="/about" className="mobile-menu__link" onClick={onClose}>
            {t.nav.about}
          </Link>
          <Link to="/contact" className="mobile-menu__link" onClick={onClose}>
            {t.nav.contact}
          </Link>

          {/* Language Selector */}
          <div className="mobile-menu__language">
            <button
              className={`mobile-menu__lang ${language === 'en' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('en')}
            >
              EN
            </button>
            <span className="mobile-menu__lang-separator">/</span>
            <button
              className={`mobile-menu__lang ${language === 'uk' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('uk')}
            >
              UA
            </button>
          </div>

          {/* Contact Icons */}
          <div className="mobile-menu__contact">
            <a
              href={`mailto:${CONTACT_INFO.email}`}
              className="mobile-menu__contact-link"
              aria-label="Email"
            >
              <Mail size={22} strokeWidth={1.5} />
            </a>
            <a
              href={CONTACT_INFO.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mobile-menu__contact-link"
              aria-label="Instagram"
            >
              <InstagramIcon size={22} />
            </a>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default MobileMenu;
